import {
  Box,
  Chip,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { SearchHit } from '../../services/searchApi';
import { effectiveCpr } from '../../utils/pricing';
import type { SortDir, SortOption } from '../../utils/searchUrl';

/**
 * Dense table view of search results, sibling to ListingCard's grid
 * variant. Used when the user toggles the search-results layout from
 * cards → table.
 *
 * Column set is the subset of attributes shoppers compare across
 * listings: title (with retailer link-out), brand, caliber, grain,
 * round count, stock state, current price, and price per round.
 *
 * Sorting — all columns that map to a supported server sort key are
 * interactive. Clicking an inactive column header switches the sort to
 * that column (resetting to its natural default direction). Clicking the
 * already-active column header toggles asc ↔ desc. Both operations
 * rewrite `?sort=…` (and `?sortDir=…` when non-default) so the URL is
 * the single source of truth — card view and table view stay in sync.
 *
 * Columns that have no server sort key (Product, Brand, Caliber, Grain,
 * Price, Stock, Updated) render plain text in the `<th>` with no sort
 * affordance at all — no dead TableSortLabel, no misleading chevron.
 *
 * Direction defaults per sort key:
 *   pricePerRound → asc   (cheapest first — most common shopper intent)
 *   roundCount    → desc  (largest pack first)
 *   newest        → desc  (most recent first)
 *   retailerTrust → desc  (highest trust first)
 *
 * Per spec §16: every header is a real `<th scope="col">`. MUI's
 * TableSortLabel manages aria-sort on the active column automatically —
 * we do NOT set aria-sort manually on TableCell to avoid double-
 * announcement.
 *
 * Per spec §17: each row's product link goes to the local /products
 * detail page, NOT the retailer's outbound URL. A separate "Visit
 * retailer" link in the title cell makes the third-party-purchase
 * disclaimer explicit.
 */

const HEADERS: ReadonlyArray<{
  key: string;
  label: string;
  sort?: SortOption;
  align?: 'left' | 'right';
}> = [
  { key: 'title', label: 'Product' },
  { key: 'brand', label: 'Brand' },
  { key: 'caliber', label: 'Caliber' },
  { key: 'grain', label: 'Grain', align: 'right' },
  { key: 'roundCount', label: 'Rounds', sort: 'roundCount', align: 'right' },
  { key: 'price', label: 'Price', align: 'right' },
  { key: 'pricePerRound', label: '$/round', sort: 'pricePerRound', align: 'right' },
  { key: 'stockStatus', label: 'Stock' },
  { key: 'retailer', label: 'Retailer', sort: 'retailerTrust' },
  { key: 'lastUpdated', label: 'Updated' },
];

export interface ListingTableProps {
  hits: SearchHit[];
  sort: SortOption;
  sortDir: SortDir;
  onSortChange: (next: SortOption) => void;
}

/**
 * @param {ListingTableProps} props
 * @returns {JSX.Element}
 */
export function ListingTable({ hits, sort, sortDir, onSortChange }: ListingTableProps) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" aria-label="Search results — table view">
        <TableHead>
          <TableRow>
            {HEADERS.map((h) => {
              const isActive = h.sort === sort;
              // When this column is active, show the live direction.
              // When inactive, show the direction a click would produce
              // (the column's natural default), so the chevron points the
              // right way on hover before the user has clicked.
              const direction = isActive ? sortDir : undefined;
              return (
                <TableCell
                  key={h.key}
                  align={h.align ?? 'left'}
                  scope="col"
                  sx={{ fontWeight: 600 }}
                  // aria-sort is managed by MUI TableSortLabel — do NOT also
                  // set it here, which would cause a double-announcement.
                >
                  {h.sort ? (
                    <TableSortLabel
                      active={isActive}
                      direction={direction}
                      onClick={() => h.sort && onSortChange(h.sort)}
                    >
                      {h.label}
                    </TableSortLabel>
                  ) : (
                    h.label
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {hits.map((hit) => (
            <TableRow key={hit.listingId} hover>
              <TableCell>
                <Stack hit={hit} />
              </TableCell>
              <TableCell>{hit.attributes.brand ?? '—'}</TableCell>
              <TableCell>{hit.attributes.caliber ?? '—'}</TableCell>
              <TableCell align="right">
                {hit.attributes.bulletWeightGrains != null
                  ? `${hit.attributes.bulletWeightGrains} gr`
                  : '—'}
              </TableCell>
              <TableCell align="right">
                {hit.roundCount != null ? hit.roundCount.toLocaleString() : '—'}
              </TableCell>
              <TableCell align="right">{formatPrice(hit.price)}</TableCell>
              <TableCell align="right">{formatPricePerRound(effectiveCpr(hit))}</TableCell>
              <TableCell>
                <StockChip stock={hit.stockStatus} />
              </TableCell>
              <TableCell>
                <RetailerCell hit={hit} />
              </TableCell>
              <TableCell>
                <FreshnessChip freshness={hit.dataFreshness} updated={hit.lastUpdated} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * Title cell. Top line links to our local product detail; second line
 * carries the explicit outbound retailer link with the §17 disclaimer
 * already covered by the retailer-comparison header on that page.
 */
function Stack({ hit }: { hit: SearchHit }) {
  const productPath = hit.productSlug
    ? `/products/${hit.productSlug}`
    : hit.productId
      ? `/products/${hit.productId}`
      : null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {productPath ? (
        <MuiLink component={RouterLink} to={productPath} underline="hover" sx={{ fontWeight: 600 }}>
          {hit.title ?? 'Untitled listing'}
        </MuiLink>
      ) : (
        <Typography component="span" sx={{ fontWeight: 600 }}>
          {hit.title ?? 'Untitled listing'}
        </Typography>
      )}
      {hit.isSponsored ? (
        <Chip
          label="Sponsored"
          size="small"
          color="secondary"
          aria-label="Sponsored"
          sx={{ alignSelf: 'flex-start', mt: 0.5 }}
        />
      ) : null}
    </Box>
  );
}

function RetailerCell({ hit }: { hit: SearchHit }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography component="span" variant="body2">
        {hit.retailer?.name ?? '—'}
      </Typography>
      {hit.visitUrl ? (
        <MuiLink href={hit.visitUrl} underline="hover" variant="caption">
          Visit retailer ↗
        </MuiLink>
      ) : null}
    </Box>
  );
}

type ChipDef = { label: string; color: 'success' | 'warning' | 'error' | 'default' };

const STOCK_LABELS: Record<string, ChipDef> = {
  in_stock: { label: 'In stock', color: 'success' },
  limited: { label: 'Limited', color: 'warning' },
  out_of_stock: { label: 'Out of stock', color: 'error' },
  unknown: { label: 'Unknown', color: 'default' },
};

const FRESHNESS_LABELS: Record<string, ChipDef> = {
  fresh: { label: 'Fresh', color: 'success' },
  aging: { label: 'Aging', color: 'warning' },
  stale: { label: 'Stale', color: 'error' },
  unavailable: { label: 'Unavailable', color: 'default' },
};

function StockChip({ stock }: { stock: SearchHit['stockStatus'] }) {
  const entry = STOCK_LABELS[stock] ?? STOCK_LABELS.unknown!;
  return <Chip size="small" label={entry.label} color={entry.color} />;
}

function FreshnessChip({
  freshness,
  updated,
}: {
  freshness: SearchHit['dataFreshness'];
  updated: SearchHit['lastUpdated'];
}) {
  const entry = FRESHNESS_LABELS[freshness] ?? FRESHNESS_LABELS.unavailable!;
  const tooltip = updated ? new Date(updated).toLocaleString() : undefined;
  return (
    <Chip
      size="small"
      label={entry.label}
      color={entry.color}
      data-testid="freshness-chip"
      title={tooltip}
    />
  );
}

function formatPrice(price: number | null | undefined): string {
  if (price == null || !Number.isFinite(price)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price);
}

function formatPricePerRound(cpr: number | null | undefined): string {
  if (cpr == null || !Number.isFinite(cpr)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(cpr);
}

export default ListingTable;
